/**
 * @author Refactored for better maintainability
 * @file customFilterComponent.tsx
 * @desc Custom filter component - main entry point
 * @copyright All rights reserved @ Inarix
 */
import React, { useState, useRef } from "react";
import { Box } from "@mui/material";
import { customFilterFormStyles } from "../dataTable/table/customFilterForm/CustomFilterFormStyles";
import { useSelector } from "react-redux";
import { RootState } from "../../configureStore";

// Types
import { CustomFilterFormProps } from "./types";

// Hooks
import { useFilterState } from "./hooks/useFilterState";
import { useApiOptions } from "./hooks/useApiOptions";
import { useClickOutside } from "./hooks/useClickOutside";
import { useFilterApply } from "./hooks/useFilterApply";

// Components
import { FilterHeader } from "./components/FilterHeader";
import { FilterRow } from "./components/FilterRow";
import { FilterActions } from "./components/FilterActions";

const CustomFilterForm: React.FC<CustomFilterFormProps> = ({
  columns,
  filterModel,
  onFilterChange,
  onApplyFilters,
  onClose,
  t,
  preselectedField,
  setPreselectedFilterField,
  comesFromDashboard,
  statusOptions = [],
  sectionName,
  position,
}) => {
  const [inputValues, setInputValues] = useState<{ [key: number]: string }>({});
  const formRef = useRef<HTMLDivElement>(null);

  // Redux selectors
  const locations = useSelector(
    (state: RootState) =>
      state.CreateTablesUsersReducer.usersSBFilterValues.locations,
  );

  const actions = useSelector(
    (state: RootState) =>
      state.CreateTablesUsersReducer.usersSBFilterValues.actions,
  );

  const loadingFilters = useSelector(
    (state: RootState) =>
      state.CreateTablesUsersReducer.loadingUsersSbFiltersValues,
  );

  // Custom hooks
  const {
    filterItems,
    handleFieldChange,
    handleOperatorChange,
    handleValueChange,
    handleChipValueChange,
    addFilter,
    removeFilter,
    clearAllFilters,
  } = useFilterState({
    filterModel,
    columns,
    locations,
    actions,
    comesFromDashboard,
    preselectedField,
    setPreselectedFilterField,
  });

  const { apiOptions } = useApiOptions({
    filterItems,
    columns,
    locations,
    actions,
  });

  const { handleApplyFilter, handleClearAll } = useFilterApply({
    filterItems,
    columns,
    apiOptions,
    comesFromDashboard,
    onFilterChange,
    onApplyFilters,
    clearAllFilters,
  });

  useClickOutside(formRef, onClose);

  const handleInputValueChange = (index: number, value: string) => {
    setInputValues((prev) => ({ ...prev, [index]: value }));
  };

  return (
    <Box
      ref={formRef}
      onClick={(e) => e.stopPropagation()}
      sx={{
        ...customFilterFormStyles.container,
        ...(position && { top: position.top, left: position.left }),
      }}
    >
      <FilterHeader t={t} comesFromDashboard={comesFromDashboard} />

      {filterItems.map((filterItem, index) => (
        <FilterRow
          key={`filter-${index}-${filterItem.field}`}
          filterItem={filterItem}
          index={index}
          columns={columns}
          t={t}
          apiOptions={apiOptions}
          inputValues={inputValues}
          loadingFilters={loadingFilters}
          statusOptions={statusOptions}
          comesFromDashboard={comesFromDashboard}
          onFieldChange={handleFieldChange}
          onOperatorChange={handleOperatorChange}
          onValueChange={handleValueChange}
          onChipValueChange={handleChipValueChange}
          onInputValueChange={handleInputValueChange}
          onRemove={removeFilter}
          filterItemsLength={filterItems.length}
        />
      ))}

      <FilterActions
        t={t}
        onAddFilter={addFilter}
        onClearAll={handleClearAll}
        onApplyFilter={handleApplyFilter}
      />
    </Box>
  );
};

export default CustomFilterForm;
